import { useCallback, useEffect, useState } from 'preact/hooks';
import { IMenuItem, IMenuState, IPendingPathsState } from './types';
import {
	resetColumnsFromDepth,
	setActiveID,
	setSubmenuParent,
	setSubmenuItems,
	transformMenuData,
	findPathByPageId,
} from './utilities';
import useMenuData from './useMenuData';
import MenuColumn from './components/MenuColumn';
import localizedData from '../localizedData';
const { __ } = wp.i18n;

export default () => {
	const [ columns, setColumns ] = useState<Array<IMenuState>>( [
		{ activeID: -1, items: [], depth: 0, parentItem: null },
		{ activeID: -1, items: [], depth: 1, parentItem: null },
		{ activeID: -1, items: [], depth: 2, parentItem: null },
		{ activeID: -1, items: [], depth: 3, parentItem: null },
		{ activeID: -1, items: [], depth: 4, parentItem: null },
		{ activeID: -1, items: [], depth: 5, parentItem: null },
		{ activeID: -1, items: [], depth: 6, parentItem: null },
		{ activeID: -1, items: [], depth: 7, parentItem: null },
		{ activeID: -1, items: [], depth: 8, parentItem: null },
		{ activeID: -1, items: [], depth: 9, parentItem: null },
		{ activeID: -1, items: [], depth: 10, parentItem: null },
		{ activeID: -1, items: [], depth: 11, parentItem: null },
	] );
	const [ activeColumn, setActiveColumn ] = useState( 0 );
	const { menuData, setMenuData, error } = useMenuData();
	const [ pendingPaths, setPendingPaths ] = useState<IPendingPathsState>( { depth: 0, path: [] } );

	const handleSubmenuToggle = useCallback( ( depth, menuItemID ) => {
		if ( ! menuData ) return;

		let newActiveID = -1;
		let newSubMenuItems = {};
		let clickedItem: IMenuItem = null;

		const toggleDirection: 'open' | 'close' = columns[depth].activeID !== menuItemID
			? 'open'
			: 'close';

		// If clicking a menu item that isn't currently opened
		if ( toggleDirection === 'open' ) {

			const clickedColumnItems = columns[depth].items;
			clickedItem = clickedColumnItems.find( ( { ID } ) => ID === menuItemID );
			newActiveID = menuItemID;

			// Check if the clicked item actually has children and skip updating
			// the active column and menu ID if so.
			// Normally, user interaction cannot even call handleSubmenuToggle
			// on a menu item that has no children as such items are links and not
			// buttons.
			// But on page load, the handler for pendingPath state force-calls
			// handleSubmenuToggle on every item on the currently openend menu items
			// path, even if it is a link without regularly attached event handler.
			// In this case skip every action regarding opening this menu item.
			if ( ! clickedItem.hasChildren ) return;
			setActiveColumn( depth + 1 );

			/**
			 * If the depthh is >= 11, there can't be any submenu items in the next column,
			 * since it is the last possible column already.
			 */
			if ( depth < 11 ) {
				newSubMenuItems = menuData;

				for ( let i = 0; i < depth; i++ ) {
					newSubMenuItems = Object.values<IMenuItem>( newSubMenuItems ?? {} )
						.find( obj => obj.ID === columns[i].activeID )?.children;
				}

				const children = Object.values<IMenuItem>( newSubMenuItems ?? {} )
					.find( obj => obj.ID === menuItemID )?.children ?? {};
				if ( children ) {
					newSubMenuItems = children;
				}

			}
		} else if ( toggleDirection === 'close' ) {
			setActiveColumn( depth );
		}

		setActiveID( depth, newActiveID, setColumns );

		/**
		 * From depth 11 on, there are no submenu items, no submenu parent to be set
		 * and there is also no columns to be reset, since we're already at the last column.
		 */
		if ( depth < 11 ) {
			if ( newSubMenuItems ) {
				setSubmenuItems( depth, newSubMenuItems, setColumns );
				setSubmenuParent( depth, clickedItem, setColumns );
			}
			resetColumnsFromDepth( depth, setColumns, toggleDirection );
		}
	}, [ columns, menuData ] );

	// On menuData initialization or update
	useEffect( () => {
		if ( menuData ) {
			const setInitialColumns = ( data ) => {
				setColumns( ( prevState ) => {
					const newColumns = [ ...prevState ];
					newColumns[0] = {
						...prevState[0],
						items: transformMenuData( data ),
					};
					return newColumns;
				} );
			};

			setMenuData( menuData );
			setInitialColumns( menuData );

			// Check if pageId is set
			const pageId = localizedData.menuApiUriData.currentPageId;
			if ( pageId ) {
				const path = findPathByPageId( {
					children: [ ...menuData ],
					ID: 0,
					title: null,
					menu_item_parent: "-1",
					hasChildren: true,
					object: "",
				} as IMenuItem, parseInt( pageId ) );

				if ( path ) {
					setPendingPaths( ( prevState ) => ( { ...prevState, path } ) ) ;
				}
			}
		}
	}, [ menuData, setMenuData ] );

	// Handle pendingPaths
	useEffect( () => {
		if ( pendingPaths.path.length > 0 && pendingPaths.depth < 11 ) {
			const [ nextId, ...remainingIds ] = pendingPaths.path;
			setPendingPaths( { depth: pendingPaths.depth + 1, path: remainingIds } ) ;
			handleSubmenuToggle( pendingPaths.depth, nextId );
		}
	}, [ pendingPaths.path, pendingPaths.depth, handleSubmenuToggle ] );

	if ( error ) {
		return <>{__( 'No menu items / Menu not existing', 'ufr-main-theme' )}</>;
	}

	const transformClasses = {
		0: "translate-x-0",
		1: "-translate-x-[calc(100%+1rem)] md:translate-x-0",
		2: "-translate-x-[calc(200%+2rem)] md:-translate-x-[calc(50%+0.5rem)] xl:translate-x-0",
		3: "-translate-x-[calc(300%+3rem)] md:-translate-x-[calc(100%+1rem)] xl:translate-x-0",
		4: "-translate-x-[calc(400%+4rem)] md:-translate-x-[calc(150%+1.5rem)] xl:-translate-x-[calc(25%+0.25rem)]",
		5: "-translate-x-[calc(500%+5rem)] md:-translate-x-[calc(200%+2rem)] xl:-translate-x-[calc(50%+0.5rem)]",
		6: "-translate-x-[calc(600%+6rem)] md:-translate-x-[calc(250%+2.5rem)] xl:-translate-x-[calc(75%+0.75rem)]",
		7: "-translate-x-[calc(700%+7rem)] md:-translate-x-[calc(300%+3rem)] xl:-translate-x-[calc(100%+1rem)]",
		8: "-translate-x-[calc(800%+8rem)] md:-translate-x-[calc(350%+3.5rem)] xl:-translate-x-[calc(125%+1.25rem)]",
		9: "-translate-x-[calc(900%+9rem)] md:-translate-x-[calc(400%+4rem)] xl:-translate-x-[calc(150%+1.5rem)]",
		10: "-translate-x-[calc(1000%+10rem)] md:-translate-x-[calc(450%+4.5rem)] xl:-translate-x-[calc(175%+1.75rem)]",
		11: "-translate-x-[calc(1100%+11rem)] md:-translate-x-[calc(500%+5rem)] xl:-translate-x-[calc(200%+2rem)]",
	};

	return (
		columns[0].items && (
			<div className="w-full h-full overflow-hidden">
				<div
					className={`
                        h-full flex gap-4 transition-transform duration-300
                        ${transformClasses[activeColumn]}
                    `}>
					{columns.map( ( column, idx ) => (
						<MenuColumn
							key={idx}
							column={column}
							activeColumn={activeColumn}
							handleSubmenuToggle={handleSubmenuToggle}
						/>
					) )}
				</div>
			</div>
		)
	);
};
